import React, { useState, useRef } from 'react';
import { StyleSheet, Text, View, TouchableOpacity, Alert, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { savePassportToken, saveUserProfile } from '@/services/auth/tokenStore';
import { fetchStreamToken, registerDeviceToken, fetchUserProfile } from '@/services/universe/client';
import { getStreamClient } from '@/services/stream/client';
import { registerForPushNotificationsAsync } from '@/utils/registerForPushNotificationsAsync';

export default function ScannerScreen() {
  const [permission, requestPermission] = useCameraPermissions();
  const [scanned, setScanned] = useState(false);
  const [loading, setLoading] = useState(false);
  const isScanning = useRef(false);
  const router = useRouter();

  if (!permission) {
    // Camera permissions are still loading.
    return <View />;
  }

  if (!permission.granted) {
    // Camera permissions are not granted yet.
    return (
      <View style={styles.container}>
        <Text style={styles.message}>We need your permission to show the camera</Text>
        <TouchableOpacity onPress={requestPermission} style={styles.button}>
          <Text style={styles.buttonText}>Continue</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const handleBarCodeScanned = async ({ type, data }: { type: string, data: string }) => {
    if (isScanning.current || scanned) return;
    
    isScanning.current = true;
    setScanned(true);
    setLoading(true);
    
    console.log("QR Code Scanned, starting authentication process...");

    try {
        if (!data || typeof data !== 'string') {
            throw new Error("Invalid QR Code: No data found");
        }
        console.log('Processing data:', data);
        
        let parsedData;
        try {
            parsedData = JSON.parse(data);
        } catch (e) {
            throw new Error("Invalid QR Code: Format error");
        }

        const passportToken = parsedData.access_token;
        console.log('Passport token extracted:', passportToken ? "YES" : "NO");

        if (!passportToken) {
            throw new Error("Invalid QR Code: access_token missing");
        }

        // Save tokens to SecureStore
        await savePassportToken(passportToken);
        
        // Verify token and get stream credentials
        const { streamUserId, streamToken } = await fetchStreamToken();
        console.log('Stream credentials fetched:', streamUserId ? "YES" : "NO");

        if (!streamUserId || !streamToken) {
            throw new Error("Failed to obtain stream credentials");
        }

        // Fetch and save user profile data
        try {
            const profileData = await fetchUserProfile();
            await saveUserProfile(profileData.name, profileData.image);
            console.log('User profile fetched and saved');
        } catch (profileError) {
            console.warn('Failed to fetch/save user profile:', profileError);
        }

        // Register device for push notifications
        try {
            const pushToken = await registerForPushNotificationsAsync();
            if (pushToken) {
                await registerDeviceToken(pushToken.expoPushToken);
                console.log('Device registered for push notifications successfully');
            }
        } catch (pushError) {
            console.warn('Failed to register for push notifications:', pushError);
            // We don't block login if push registration fails
        }

        // Re-evaluate auth in RootLayout
        router.replace('/(tabs)');
        
    } catch (error: any) {
        console.warn("Authentication process error:", error);
        
        const errorMessage = error?.message || "An unexpected error occurred during authentication. Please try again.";
        const title = error?.message?.includes("Invalid QR Code") ? "Invalid QR Code" : "Authentication Error";

        Alert.alert(
            title,
            errorMessage,
            [{ 
                text: "OK", 
                onPress: () => {
                    setScanned(false);
                    isScanning.current = false;
                    setLoading(false);
                } 
            }]
        );
    }
  };

  return (
    <View style={styles.container}>
      {!scanned ? (
        <CameraView
          style={StyleSheet.absoluteFillObject}
          onBarcodeScanned={handleBarCodeScanned}
          barcodeScannerSettings={{
            barcodeTypes: ["qr"],
          }}
        />
      ) : (
        <View style={[StyleSheet.absoluteFillObject, { backgroundColor: 'black' }]} />
      )}
      <SafeAreaView style={styles.overlay}>
          <View style={styles.header}>
            <TouchableOpacity onPress={() => router.back()} style={styles.backButton}>
              <MaterialCommunityIcons name="close" size={28} color="white" />
            </TouchableOpacity>
            <Text style={styles.headerTitle}>Scan QR Code</Text>
            <View style={{ width: 28 }} />
          </View>

          <View style={styles.content}>
            <View style={styles.scanArea}>
                <View style={[styles.corner, styles.topLeft]} />
                <View style={[styles.corner, styles.topRight]} />
                <View style={[styles.corner, styles.bottomLeft]} />
                <View style={[styles.corner, styles.bottomRight]} />
                {loading && (
                    <View style={styles.loadingOverlay}>
                        <ActivityIndicator size="large" color={Colors.brand} />
                        <Text style={styles.loadingText}>Authenticating...</Text>
                    </View>
                )}
            </View>
            <Text style={styles.instructionText}>
                Center the QR code within the frame
            </Text>
          </View>

          {scanned && !loading && (
            <TouchableOpacity 
                style={styles.rescanButton} 
                onPress={() => {
                    setScanned(false);
                    isScanning.current = false;
                }}
            >
              <Text style={styles.rescanButtonText}>Tap to Scan Again</Text>
            </TouchableOpacity>
          )}
        </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: 'black',
  },
  message: {
    textAlign: 'center',
    paddingBottom: 10,
    color: 'white',
    fontSize: 16,
  },
  button: {
    backgroundColor: Colors.brand,
    padding: 15,
    borderRadius: 8,
    alignSelf: 'center',
  },
  buttonText: {
    color: 'white',
    fontWeight: 'bold',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'space-between',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 10,
  },
  headerTitle: {
    color: 'white',
    fontSize: 18,
    fontWeight: '600',
  },
  backButton: {
    padding: 5,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  scanArea: {
    width: 250,
    height: 250,
    borderWidth: 0,
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 20,
  },
  loadingText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
    marginTop: 10,
  },
  corner: {
    position: 'absolute',
    width: 40,
    height: 40,
    borderColor: 'white',
    borderWidth: 4,
  },
  topLeft: {
    top: 0,
    left: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderTopLeftRadius: 20,
  },
  topRight: {
    top: 0,
    right: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
    borderTopRightRadius: 20,
  },
  bottomLeft: {
    bottom: 0,
    left: 0,
    borderRightWidth: 0,
    borderTopWidth: 0,
    borderBottomLeftRadius: 20,
  },
  bottomRight: {
    bottom: 0,
    right: 0,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderBottomRightRadius: 20,
  },
  instructionText: {
    color: 'white',
    fontSize: 16,
    marginTop: 30,
    textAlign: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    overflow: 'hidden',
  },
  rescanButton: {
    backgroundColor: 'white',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 30,
    alignSelf: 'center',
    marginBottom: 40,
  },
  rescanButtonText: {
    color: 'black',
    fontSize: 16,
    fontWeight: 'bold',
  },
});
